
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { MaterialFormData } from '../forms/MaterialFormData';
import { useCurrentOrganization } from '@/contexts/OrganizationContext';

export const useMaterialSubmit = (onOpenChange: (open: boolean) => void, onSuccess: () => void) => {
  const queryClient = useQueryClient();
  const { currentOrganizationId } = useCurrentOrganization();

  const { mutate: submitMaterial, isPending: isSubmitting } = useMutation({
    mutationFn: async (formData: MaterialFormData & { id?: string; isCatalogMaterial?: boolean }) => {
      console.log('[useMaterialSubmit] 🚀 SUBMITTING MATERIAL:', formData);

      // unit_price viene RICALCOLATO dal trigger DB sulla base di list_price,
      // catena famiglia (customer_discounts) e materials.extra_discount.
      // Qui salviamo solo i valori sorgente; il netto viene fuori dalla view materials_with_pricing.
      const finalUnitPrice = parseFloat(formData.unit_price || '0');

      // Prepara i dati per l'inserimento/aggiornamento
      const materialData = {
        code: formData.code,
        name: formData.name,
        description: formData.description || null,
        category: formData.category,
        supplier: formData.supplier,
        // F-family-2026-05-22: FK alla famiglia del fornitore. Permette il
        // calcolo automatico di net_price con sconti cumulativi della famiglia
        // (vista materials_with_pricing).
        family_id: formData.family_id ?? null,
        thickness: formData.thickness ? parseFloat(formData.thickness) : null,
        width: formData.width ? parseFloat(formData.width) : null,
        length: formData.length ? parseFloat(formData.length) : null,
        weight_per_sqm: formData.weight_per_sqm ? parseFloat(formData.weight_per_sqm) : null,
        unit_price: finalUnitPrice, // sarà sovrascritto dal trigger DB se cambia extra_discount
        list_price: formData.list_price ? parseFloat(formData.list_price) : null,
        // discount (TEXT legacy) NON più gestito dal frontend — lasciato a NULL al posto del valore
        discount: null,
        extra_discount: formData.extra_discount ? parseFloat(formData.extra_discount) : 0,
        unit: formData.unit,
        thermal_conductivity: formData.thermal_conductivity ? parseFloat(formData.thermal_conductivity) : null,
        acoustic_performance: formData.acoustic_performance ? parseFloat(formData.acoustic_performance) : null,
        fire_resistance_class: formData.fire_resistance_class || null,
        color_hex: formData.color_hex || '#CCCCCC',
        incidence_base: formData.incidence_base ? parseFloat(formData.incidence_base) : null,
        incidence_per_sqm: formData.incidence_per_sqm ? parseFloat(formData.incidence_per_sqm) : 1,
        // Il form lavora in MINUTI (come pensa l'utente), il DB memorizza in ORE
        // (i motori di calcolo costo fanno ×60). Converto minuti → ore in salvataggio.
        installation_time_per_sqm: formData.installation_time_per_sqm
          ? Math.round((parseFloat(formData.installation_time_per_sqm) / 60) * 1e6) / 1e6
          : null,
        // Campi specifici per categoria
        passo: formData.passo ? parseFloat(formData.passo) : null,
        material_type: formData.material_type || null,
        board_typology: formData.board_typology || null,
        density: formData.density ? parseFloat(formData.density) : null,
        flexural_strength: formData.flexural_strength || null,
        surface_hardness: formData.surface_hardness || null,
        en_520_type: formData.en_520_type || null,
        water_absorption: formData.water_absorption || null,
        humidity_resistance_class: formData.humidity_resistance_class || null,
        environmental_certification: formData.environmental_certification || null,
        recycled_content: formData.recycled_content ? parseFloat(formData.recycled_content) : null,
        voc_class: formData.voc_class || null,
        rei_compatible: formData.rei_compatible === 'true',
        intended_use: Array.isArray(formData.intended_use) ? formData.intended_use : [formData.intended_use].filter(Boolean),
        installation_notes: formData.installation_notes || null,
        fire_class: formData.fire_class || null,
        fire_description: formData.fire_description || null,
        board_type: formData.board_type || null,
        fire_usage_notes: formData.fire_usage_notes || null,
        sheet_thickness: formData.sheet_thickness ? parseFloat(formData.sheet_thickness) : null,
        weight_per_ml: formData.weight_per_ml ? parseFloat(formData.weight_per_ml) : null,
        profile_type: formData.profile_type || null,
        surface_finish: formData.surface_finish || null,
        // Campi per "Altro"
        is_variable_thickness: formData.is_variable_thickness || false,
        mechanical_performance: formData.mechanical_performance || null,
        thermal_performance_notes: formData.thermal_performance_notes || null,
        sustainability_notes: formData.sustainability_notes || null,
        system_compatibility: formData.system_compatibility || null,
        fire_performance_notes: formData.fire_performance_notes || null,
        carbon_footprint: formData.carbon_footprint || null,
        epd: formData.epd || null,
        vapor_permeability: formData.vapor_permeability || null,
        thermal_capacity: formData.thermal_capacity || null,
        // Campi per viti
        box_pieces: formData.box_pieces ? parseInt(formData.box_pieces) : null,
        compatible_board_types: formData.compatible_board_types ? 
          formData.compatible_board_types.split(',').map(s => s.trim()).filter(Boolean) : null,
        // Campi per sfrido e discarica.
        // waste_percentage: vuoto = NULL = usa default categoria (Settings → Sfridi).
        waste_percentage: formData.waste_percentage && formData.waste_percentage.trim() !== ''
          ? parseFloat(formData.waste_percentage)
          : null,
        disposal_percentage: formData.disposal_percentage ? parseFloat(formData.disposal_percentage) : 4,
      };

      // ===================================================================
      // CAMPI DI CLASSIFICAZIONE: si scrivono SOLO dove il form li mostra.
      //
      // `initialFormData` porta dei default non vuoti — material_type
      // 'gesso_rivestito', board_typology 'standard', sheet_thickness '0.6',
      // profile_type 'C', surface_finish 'zincatura_z140' — e il payload li
      // scriveva per QUALSIASI categoria, anche quando il form non li mostra
      // nemmeno. Cosi' 36 lastre e 18 isolanti si sono ritrovati con
      // surface_finish='zincatura_z140', e ogni profilo nuovo nasceva
      // classificato come "gesso rivestito".
      //
      // Si OMETTE la chiave, non la si manda a `null`: su PostgREST una chiave
      // assente lascia il valore com'e', una chiave a null lo cancella. Cosi'
      // cio' che ha scritto l'import resta intatto, perche' il form non puo'
      // modificare un campo che non mostra.
      const RENDERED_BY: Record<string, string[]> = {
        material_type:   ['board', 'ceiling_tile', 'insulation', 'accessory', 'finish'],
        board_typology:  ['board', 'ceiling_tile', 'insulation'],
        sheet_thickness: ['structure_frame', 'structure_guide'],
        profile_type:    ['structure_frame', 'structure_guide'],
        surface_finish:  ['structure_frame', 'structure_guide'],
      };
      for (const [field, categories] of Object.entries(RENDERED_BY)) {
        if (!categories.includes(formData.category)) {
          delete (materialData as Record<string, unknown>)[field];
        }
      }

      console.log('[useMaterialSubmit] 📊 PREPARED DATA:', materialData);

      // SINGLE SOURCE OF TRUTH per i fornitori: se l'utente ha digitato un
      // fornitore non ancora presente in `suppliers`, creo la riga al volo.
      // Senza questo step `materials.supplier` può divergere da `suppliers.name`
      // (è successo con "Fornitore Locale": appariva nel filtro Materiali ma
      // non nella Dashboard, perché la Dashboard pesca da `suppliers`).
      if (formData.supplier && formData.supplier.trim()) {
        const supplierName = formData.supplier.trim();
        const { data: existing } = await supabase
          .from('suppliers')
          .select('id')
          .ilike('name', supplierName)
          .maybeSingle();
        if (!existing) {
          const slug = supplierName.toLowerCase().replace(/[^a-z0-9]+/g, '-');
          const { error: supErr } = await supabase
            .from('suppliers')
            .insert({
              name: supplierName,
              slug,
              organization_id: null, // promosso a globale: coerente coi 4 esistenti
              is_active: true,
              country_code: 'IT',
              is_system: false,
            });
          if (supErr) {
            console.warn('[useMaterialSubmit] auto-upsert fornitore fallito:', supErr);
            // Non blocchiamo il salvataggio del materiale: il fornitore può
            // essere creato manualmente da /suppliers se serve.
          } else {
            console.log('[useMaterialSubmit] 🆕 fornitore creato automaticamente:', supplierName);
          }
        }
      }

      // ⚠️ MAI scrivere il NETTO su un materiale del catalogo condiviso.
      //
      // `unit_price` qui non e` il prezzo di catalogo: `MaterialPricingSection`
      // lo ricalcola live come `list_price x catena famiglia x (1 - extra)`, e
      // quella catena e` quella dell'ORGANIZZAZIONE CORRENTE (la vista risolve
      // `customer_discounts` preferendo le righe dell'org). Anche
      // `catalog_net_price`, da cui il form parte, toglie l'override per-materiale
      // ma NON gli sconti famiglia.
      //
      // Su un materiale globale il trigger di ricalcolo non interviene (filtra per
      // organization_id, che li` e` NULL): il valore scritto resta. Risultato: un
      // admin che corregge un dato qualsiasi pubblicherebbe a TUTTE le aziende il
      // prezzo scontato della propria. Il netto non serve comunque: la vista lo
      // ricava da `list_price`, che e` uguale per tutti.
      if (formData.id && formData.isCatalogMaterial) {
        delete (materialData as Record<string, unknown>).unit_price;
      }

      let result;
      if (formData.id) {
        // Aggiornamento
        const { data, error } = await supabase
          .from('materials')
          .update(materialData)
          .eq('id', formData.id)
          .select()
          .single();
        
        if (error) throw error;
        result = data;
        console.log('[useMaterialSubmit] ✅ MATERIAL UPDATED:', result);
      } else {
        // Creazione: il materiale nasce DELL'ORGANIZZAZIONE corrente.
        // Senza organization_id finirebbe nel catalogo globale e la RLS lo
        // bloccherebbe ("Org writers can insert org materials" richiede
        // organization_id NOT NULL) → l'utente vedeva un errore incomprensibile.
        if (!currentOrganizationId) {
          throw new Error('Nessuna organizzazione selezionata: impossibile creare il materiale.');
        }
        const { data, error } = await supabase
          .from('materials')
          .insert({ ...materialData, organization_id: currentOrganizationId })
          .select()
          .single();

        if (error) throw error;
        result = data;
        console.log('[useMaterialSubmit] ✅ MATERIAL CREATED:', result);
      }

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['materials'] });
      // Invalida anche i fornitori: se questo material ha appena creato una
      // nuova riga in `suppliers` (auto-upsert sopra), la Dashboard FilterBar e
      // la pagina /suppliers devono aggiornarsi senza richiedere refresh.
      queryClient.invalidateQueries({ queryKey: ['suppliers-list'] });
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      toast.success('Materiale salvato con successo!');
      onOpenChange(false);
      onSuccess();
    },
    onError: (error: any) => {
      console.error('[useMaterialSubmit] ❌ ERROR:', error);
      toast.error('Errore nel salvare il materiale: ' + error.message);
    },
  });

  return { submitMaterial, isSubmitting };
};
